import { useEffect, useRef, useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Plus } from 'lucide-react';

export interface CrmCreateActionOption {
  id: string;
  label: string;
  description: string;
  icon: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
}

interface CrmCreateActionDropdownProps {
  options: CrmCreateActionOption[];
  /** Hide the control when there is nothing to show. */
  hidden?: boolean;
  align?: 'left' | 'right';
  menuTitle?: string;
  ariaLabel?: string;
}

/**
 * Blue + with dropdown — visual language matches BookingTypeSelectionModal
 * (calendar slot create) and the compact FAB action sheet.
 */
export default function CrmCreateActionDropdown({
  options,
  hidden = false,
  align = 'right',
  menuTitle = 'Create',
  ariaLabel = 'Create',
}: CrmCreateActionDropdownProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  if (hidden || options.length === 0) {
    return null;
  }

  const menuAlignClass = align === 'left' ? 'left-0' : 'right-0';

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setMenuOpen((open) => !open)}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow-sm transition-colors hover:bg-blue-700 active:scale-[0.98]"
        aria-label={ariaLabel}
        aria-expanded={menuOpen}
        aria-haspopup="menu"
        style={{ WebkitTapHighlightColor: 'transparent' }}
      >
        <Plus className="h-5 w-5" strokeWidth={2.5} />
      </button>

      {menuOpen ? (
        <div
          role="menu"
          className={`absolute ${menuAlignClass} top-full z-[30] mt-2 w-[min(100vw-1.5rem,17.5rem)] overflow-hidden pointer-events-auto`}
          style={{
            borderRadius: 14,
            background: 'rgba(255, 255, 255, 0.78)',
            border: '0.5px solid rgba(0, 0, 0, 0.08)',
            boxShadow:
              '0 0 0 0.5px rgba(0,0,0,0.04), 0 10px 40px rgba(0,0,0,0.18), 0 2px 10px rgba(0,0,0,0.08)',
            backdropFilter: 'blur(40px) saturate(180%)',
            WebkitBackdropFilter: 'blur(40px) saturate(180%)',
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, sans-serif',
          }}
        >
          <div className="px-3.5 pt-3 pb-2">
            <p
              className="text-[11px] font-semibold tracking-wide text-black/35"
              style={{ letterSpacing: '0.02em' }}
            >
              {menuTitle}
            </p>
          </div>

          <div className="mx-2.5 mb-2.5 overflow-hidden rounded-[10px] bg-black/[0.035]">
            {options.map((option, index) => {
              const Icon = option.icon;
              const isLast = index === options.length - 1;
              return (
                <button
                  key={option.id}
                  type="button"
                  role="menuitem"
                  disabled={option.disabled}
                  onClick={() => {
                    if (option.disabled) return;
                    setMenuOpen(false);
                    option.onSelect();
                  }}
                  className={`flex w-full items-center gap-3 px-3 py-2.5 text-left touch-manipulation transition-colors ${
                    option.disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-black/[0.04] active:bg-black/[0.08]'
                  } ${!isLast ? 'border-b border-black/[0.06]' : ''}`}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] shadow-sm ${
                      option.disabled
                        ? 'bg-gray-200 text-gray-400 shadow-none'
                        : 'bg-[#007AFF] text-white shadow-[#007AFF]/30'
                    }`}
                  >
                    <Icon className="h-[15px] w-[15px]" strokeWidth={2.25} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-[15px] font-semibold leading-tight ${
                        option.disabled ? 'text-black/40' : 'text-black/85'
                      }`}
                    >
                      {option.label}
                    </span>
                    <span className="block text-[12px] text-black/40 mt-0.5 leading-snug">
                      {option.description}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
